import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
function parseCSV(text) {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2)
        return [];
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const required = ['firstname', 'lastname'];
    const missing = required.filter((h) => !headers.includes(h));
    if (missing.length > 0) {
        return [{ firstName: '', lastName: '', _rowError: `Faltan columnas: ${missing.join(', ')}` }];
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map((v) => v.trim());
        const obj = {};
        headers.forEach((h, idx) => { obj[h] = values[idx] ?? ''; });
        rows.push({
            firstName: obj['firstname'] ?? '',
            lastName: obj['lastname'] ?? '',
            email: obj['email'] || undefined,
            phone: obj['phone'] || undefined,
            documentType: obj['documenttype'] || undefined,
            documentNumber: obj['documentnumber'] || undefined,
            seatNumber: obj['seatnumber'] || undefined,
            roomNumber: obj['roomnumber'] || undefined,
        });
    }
    return rows;
}
export function BulkImport({ groupId, onComplete }) {
    const [file, setFile] = useState(null);
    const [parsed, setParsed] = useState([]);
    const [parseError, setParseError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const handleFile = useCallback((f) => {
        setFile(f);
        setParseError(null);
        setResult(null);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result;
                const rows = parseCSV(text);
                if (rows.length === 0) {
                    setParseError('El archivo está vacío o no tiene el formato correcto');
                    return;
                }
                setParsed(rows);
            }
            catch {
                setParseError('No se pudo leer el archivo');
            }
        };
        reader.readAsText(f);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        const f = e.dataTransfer.files[0];
        if (f && (f.type === 'text/csv' || f.name.endsWith('.csv'))) {
            handleFile(f);
        }
        else {
            setParseError('Solo se aceptan archivos CSV');
        }
    }, [handleFile]);
    const handleSubmit = async () => {
        setIsSubmitting(true);
        let ok = 0;
        let fail = 0;
        for (const row of parsed.filter((r) => !r._rowError && r.firstName && r.lastName)) {
            try {
                const dto = {
                    groupId,
                    firstName: row.firstName,
                    lastName: row.lastName,
                    email: row.email,
                    phone: row.phone,
                    documentType: row.documentType,
                    documentNumber: row.documentNumber,
                    seatNumber: row.seatNumber,
                    roomNumber: row.roomNumber,
                };
                await apiClient.post('/api/v1/passengers', dto);
                ok++;
            }
            catch {
                fail++;
            }
        }
        setIsSubmitting(false);
        setResult({ ok, fail });
        if (ok > 0)
            onComplete();
    };
    const validRows = parsed.filter((r) => !r._rowError && r.firstName && r.lastName);
    const invalidRows = parsed.filter((r) => r._rowError);
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: `border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${parseError
                    ? 'border-[var(--color-error)] bg-[var(--color-error-light)]'
                    : file
                        ? 'border-[var(--tenant-primary)] bg-[var(--tenant-primary)]/5'
                        : 'border-[var(--color-border)] hover:border-[var(--tenant-primary)]'}`, onDragOver: (e) => e.preventDefault(), onDrop: handleDrop, onClick: () => document.getElementById('csv-upload')?.click(), children: [_jsx("input", { id: "csv-upload", type: "file", accept: ".csv", className: "hidden", onChange: (e) => {
                            const f = e.target.files?.[0];
                            if (f)
                                handleFile(f);
                        } }), file ? (_jsxs("div", { className: "flex items-center justify-center gap-3", children: [_jsx(FileText, { className: "w-6 h-6 text-[var(--tenant-primary)]" }), _jsxs("div", { className: "text-left", children: [_jsx("p", { className: "text-sm font-medium text-[var(--color-text)]", children: file.name }), _jsxs("p", { className: "text-xs text-[var(--color-text-muted)]", children: [parsed.length, " filas"] })] }), _jsx("button", { className: "p-1 hover:text-[var(--color-error)] transition-colors", onClick: (e) => {
                                    e.stopPropagation();
                                    setFile(null);
                                    setParsed([]);
                                    setParseError(null);
                                }, children: _jsx(X, { className: "w-4 h-4" }) })] })) : (_jsxs(_Fragment, { children: [_jsx(Upload, { className: "w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" }), _jsxs("p", { className: "text-sm text-[var(--color-text)] mb-1", children: ["Arrastra un CSV aqu\u00ED o", ' ', _jsx("span", { className: "text-[var(--tenant-primary)] font-medium cursor-pointer", children: "selecci\u00F3nalo" })] }), _jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "Columnas: firstName, lastName, email, phone, documentType, documentNumber, seatNumber, roomNumber" })] }))] }), parseError && (_jsxs("div", { className: "flex items-center gap-2 p-3 rounded-md bg-[var(--color-error-light)] text-[var(--color-error)] text-sm", children: [_jsx(AlertCircle, { className: "w-4 h-4 flex-shrink-0" }), parseError] })), validRows.length > 0 && (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("p", { className: "text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide", children: ["Vista previa (", validRows.length, " pasajeros)"] }), _jsxs("div", { className: "max-h-48 overflow-y-auto border border-[var(--color-border)] rounded-md", children: [_jsxs("table", { className: "w-full text-xs border-collapse", children: [_jsx("thead", { children: _jsxs("tr", { className: "bg-[var(--color-surface-raised)]", children: [_jsx("th", { className: "px-3 py-2 text-left font-medium text-[var(--color-text-muted)]", children: "Nombre" }), _jsx("th", { className: "px-3 py-2 text-left font-medium text-[var(--color-text-muted)]", children: "Apellido" }), _jsx("th", { className: "px-3 py-2 text-left font-medium text-[var(--color-text-muted)]", children: "Email" }), _jsx("th", { className: "px-3 py-2 text-left font-medium text-[var(--color-text-muted)]", children: "Tel\u00E9fono" })] }) }), _jsx("tbody", { children: validRows.slice(0, 20).map((row, i) => (_jsxs("tr", { style: { borderBottom: '1px solid var(--color-border)' }, children: [_jsx("td", { className: "px-3 py-2 text-[var(--color-text)]", children: row.firstName }), _jsx("td", { className: "px-3 py-2 text-[var(--color-text)]", children: row.lastName }), _jsx("td", { className: "px-3 py-2 text-[var(--color-text)]", children: row.email ?? '—' }), _jsx("td", { className: "px-3 py-2 text-[var(--color-text)]", children: row.phone ?? '—' })] }, i))) })] }), validRows.length > 20 && (_jsxs("p", { className: "px-3 py-2 text-xs text-[var(--color-text-muted)] text-center", children: ["y ", validRows.length - 20, " m\u00E1s..."] }))] })] })), invalidRows.length > 0 && (_jsxs("div", { className: "flex items-start gap-2 p-3 rounded-md bg-[var(--color-warning-light)] text-[var(--color-warning)] text-xs", children: [_jsx(AlertCircle, { className: "w-4 h-4 flex-shrink-0 mt-0.5" }), _jsxs("div", { children: [_jsxs("p", { className: "font-medium", children: ["Filas con errores (", invalidRows.length, ")"] }), invalidRows.map((r, i) => (_jsxs("p", { children: ["Fila ", i + 2, ": ", r._rowError] }, i)))] })] })), result && (_jsxs("div", { className: "flex items-center gap-2 p-3 rounded-md bg-[var(--color-success-light)] text-[var(--color-success)] text-sm", children: [_jsx(CheckCircle, { className: "w-4 h-4 flex-shrink-0" }), result.ok, " pasajeros importados, ", result.fail, " fallidos"] })), _jsxs("div", { className: "flex justify-end gap-3", children: [_jsx("button", { className: "btn-secondary btn-md", onClick: onComplete, disabled: isSubmitting, children: "Cancelar" }), _jsx("button", { className: "btn-primary btn-md", onClick: handleSubmit, disabled: isSubmitting || validRows.length === 0, children: isSubmitting
                            ? _jsx("span", { className: "spinner", style: { borderColor: 'white', borderTopColor: 'transparent' } })
                            : `Importar ${validRows.length} pasajeros` })] })] }));
}
//# sourceMappingURL=BulkImport.js.map